/**
 * The small subset of ICU MessageFormat the catalogues use, so a translator can write what every language needs
 * without code:
 *
 *   "Hello {name}"                                      named value
 *   "{count, plural, one {# agent} other {# agents}}"   plural; "#" is the count; "=0 {none}" matches exactly
 *
 * Numbers are formatted with the locale (`1,234` / `1 234`). A value that is missing stays visible as `{name}`.
 * No quoting rules: an apostrophe is an apostrophe, and a literal brace must not appear in a message.
 */
export type MessageValues = Record<string, string | number>;

/** Index of the brace closing the one opened at `open`, or -1. */
function closingBrace(text: string, open: number): number {
    let depth = 0;
    for (let i = open; i < text.length; i++) {
        if (text[i] === "{") depth++;
        else if (text[i] === "}" && --depth === 0) return i;
    }
    return -1;
}

/** `one {# agent} other {# agents}` -> { one: "# agent", other: "# agents" } */
function parseBranches(body: string): Map<string, string> {
    const branches = new Map<string, string>();
    let i = 0;
    while (i < body.length) {
        const open = body.indexOf("{", i);
        if (open < 0) break;
        const selector = body.slice(i, open).trim();
        const close = closingBrace(body, open);
        if (close < 0) break;
        branches.set(selector, body.slice(open + 1, close));
        i = close + 1;
    }
    return branches;
}

export function formatMessage(template: string, values: MessageValues | undefined, locale: string): string {
    if (!template.includes("{")) return template;
    const numberFormat = new Intl.NumberFormat(locale);
    const show = (value: string | number) => (typeof value === "number" ? numberFormat.format(value) : value);

    let out = "";
    let i = 0;
    while (i < template.length) {
        const open = template.indexOf("{", i);
        if (open < 0) {
            out += template.slice(i);
            break;
        }
        out += template.slice(i, open);
        const close = closingBrace(template, open);
        if (close < 0) {
            out += template.slice(open);
            break;
        }
        const inner = template.slice(open + 1, close);
        const comma = inner.indexOf(",");
        if (comma < 0) {
            const name = inner.trim();
            const value = values?.[name];
            out += value === undefined ? `{${name}}` : show(value);
        } else {
            const name = inner.slice(0, comma).trim();
            const rest = inner.slice(comma + 1);
            const kind = rest.slice(0, rest.indexOf(",")).trim();
            const value = values?.[name];
            if (kind !== "plural" || typeof value !== "number") {
                out += `{${name}}`;
            } else {
                const branches = parseBranches(rest.slice(rest.indexOf(",") + 1));
                const chosen =
                    branches.get(`=${value}`) ??
                    branches.get(new Intl.PluralRules(locale).select(value)) ??
                    branches.get("other") ??
                    "";
                // a branch may itself hold {values}; "#" is the plural's own count
                out += formatMessage(chosen.replaceAll("#", `{${name}}`), values, locale);
            }
        }
        i = close + 1;
    }
    return out;
}

/** The `{name}` and plural variable names a message uses, for the test that keeps languages in step. */
export function placeholders(template: string): string[] {
    const names = new Set<string>();
    const scan = (text: string) => {
        let i = 0;
        while (i < text.length) {
            const open = text.indexOf("{", i);
            if (open < 0) return;
            const close = closingBrace(text, open);
            if (close < 0) return;
            const inner = text.slice(open + 1, close);
            const comma = inner.indexOf(",");
            if (comma < 0) {
                names.add(inner.trim());
            } else {
                names.add(inner.slice(0, comma).trim());
                const rest = inner.slice(comma + 1);
                for (const branch of parseBranches(rest.slice(rest.indexOf(",") + 1)).values()) scan(branch);
            }
            i = close + 1;
        }
    };
    scan(template);
    return [...names].sort();
}
